import {ISymbol, SimpleReelModelEventData, SimpleReelModelState} from "../../core/reel/ReelInterfaces";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {gsap, TimelineLite} from "gsap";
import {TheBorderReelView} from "../view/reels/TheBorderReelView";
import {TheBorder} from "../TheBorder";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {TheBorderStatics} from "../TheBorderStatics";
import {TheBorderSound} from "../sound/TheBorderSound";

/**
 * Created by Jerker Nord on 2021-06-17.
 */

export class TheBorderReelModel {

    private _reelId:number;
    //Used for positioning, not animation (since it's avalanche)
    public stepSize:number = 100;

    private _reelView:TheBorderReelView;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     */
    public readonly events:APIEventSystem | undefined;

    private _reelSet:string[] | undefined;
    private _state:SimpleReelModelState = SimpleReelModelState.IDLE;
    private _spinning:boolean = false;
    private _masterSpinTimeline:TimelineLite | undefined;

    constructor(id:number, triggerEvents:boolean = true) {
        this._reelId = id;
        this._reelView = new TheBorderReelView(id);
        if(triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data:SimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }
    }

    private onReelStateChange(data:SimpleReelModelEventData):void {
        this.events?.trigger(data.state, data);
    }

    public clear() {
        this._reelView.clear();
    }

    public init(symbols:string[], isAvalancheRestore:boolean, isBonusIntroRestore:boolean) {
        this._reelView.initReel(symbols, isAvalancheRestore, isBonusIntroRestore);
    }

    public getSymbol(position:number):ISymbol {
        return this._reelView.getSymbol(position);
    }

    public getAllSymbols():ISymbol[] {
        return this._reelView.getAllSymbols();
    }

    public getFillUpReelAnimation():gsap.core.Timeline {
        return this.reelView.getFillUpReelAnimation();
    }

    /**
     * Calculates which symbols that should fall down and how many steps
     */
    public collapseReel():gsap.core.Timeline {
        return this._reelView.collapseReel();
    }

    public stop(reelData:string[]):TimelineLite {
        let reelInSoundPlayed:boolean = false;
        const tl:gsap.core.Timeline = gsap.timeline({
            onComplete : () => {
                this.state = SimpleReelModelState.STOPPED;
                this.state = SimpleReelModelState.IDLE;
            }
        });
        if(this._reelId == 0 && reelInSoundPlayed == false) {
            reelInSoundPlayed = true;
            tl.add(() => {TheBorder.soundPlayer.playSound(TheBorderSound.SPIN_ALL_IN)} );
        }
        tl.add(this._reelView.stop(reelData));

        return tl;
    }

    public start():TimelineLite {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {this.state = SimpleReelModelState.START});
        tl.add(() => this.startSpin());
        //Delay to make sure last reel is out before next spin starts
        if(this._reelId == 5) {
            tl.add(() => {}, "+=" + ((TheBorder.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? TheBorderStatics.mainReelAllOutTotalSpinDurationFastSpin : TheBorderStatics.mainReelAllOutTotalSpinDuration)));
        }

        return tl;
    }

    /**
     * Starting the actual spin by adding it to the masterSpinTimeline
     * @private
     */
    private startSpin() {
        this._reelView.onStartSpin();
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this._masterSpinTimeline = gsap.timeline();
        this._masterSpinTimeline.add(this.spin());
    }

    private spin():TimelineLite {
        const timeline:gsap.core.Timeline = gsap.timeline();
        let reelOutSoundPlayed:boolean = false;
        if(this._reelId == 0 && reelOutSoundPlayed == false) {
            reelOutSoundPlayed = true;
            if (this._reelView.reelHidden == false){
                timeline.add(() => {TheBorder.soundPlayer.playSound(TheBorderSound.SPIN_ALL_OUT)});
            }
        }
        timeline.add(() => {this._reelView.getAllOutAnimation()});
        timeline.add(() => this.onSpinStartedCompleted());
        return timeline;
    }

    private onSpinStartedCompleted():void {

    }

    get reelSet():string[] {
        if(!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.reelId}`);
        }
        return this._reelSet;
    }

    set state(value:SimpleReelModelState) {
        this._state = value;
        if(this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel : this.reelId, state : this._state});
        }
    }

    get reelId():number {
        return this._reelId;
    }

    get reelView():TheBorderReelView {
        return this._reelView;
    }
}